import React, { useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import {
    Stack,
} from "@mui/material";

import { Message, useGetProposedMeetingTimes } from "../../store";

import { ActiveStatus, MeetingStatus, Patient, User, UserType } from "../../types";
import MeetingTimeComponent from "../../components/meeting/time/meeting-time-component";

import MeetingTimeErrorPage from "./meeting-time-error-page";
import ClearCallLoadingLogo from "../../components/clear-call-loading-logo";
import ConfirmTime from "../../components/meeting/time/confirm-time";
import { ProposeMeetingProvider } from "../../components/meeting/time/propose-meeting-context";

const MeetingTimePage: React.FC = () => {
    const { meetingId } = useParams<{ meetingId: string }>();
    const [searchParams] = useSearchParams();

    const [userId] = useState(searchParams.get("userId") || "");
    const [joinCode] = useState(searchParams.get("joinCode") || "");
    const [message, setMessage] = useState<Message | null>(null);

    const { data: data, isLoading } = useGetProposedMeetingTimes(meetingId || "", userId, joinCode)

    const meetingData = data?.meeting

    const isCreator = meetingData?.creatorId == userId

    const { patientDictionary, userDictionary } = useMemo((): {
        patientDictionary: Record<string, Patient>
        userDictionary: Record<string, User>
    } => {
        if (!meetingData) {
            return {
                patientDictionary: {},
                userDictionary: {},
            }
        }

        const patientDictionary: Record<string, Patient> = {
            [meetingData.patientId]: {
                patientId: meetingData.patientId,
                status: ActiveStatus.Active,
                displayId: "",
                created: new Date(),
                meetingData: [],
                meetingIds: [],
            }
        }

        const userDictionary: Record<string, User> = {}

        return {
            patientDictionary,
            userDictionary,
        }
    }, [meetingData])

    const getError = (): Message | null => {
        if (!joinCode || !userId || !meetingId) {
            return {
                type: "error",
                message: "Invalid meeting link"
            }
        }

        if (meetingData && meetingData.meetingType != MeetingStatus.Proposed) {
            return {
                type: "success",
                message: "Meeting time has been finalised"
            }
        }

        if (!isLoading && !meetingData) {
            return {
                type: "error",
                message: "Fail to load data"
            }
        }

        return message
    }

    const error = getError()

    if (isLoading) {
        return (<ClearCallLoadingLogo />)
    }

    if (error) {
        return (
            <MeetingTimeErrorPage message={error} />
        )
    }

    return (
        <Stack
            justifyContent={"center"}
            justifyItems={"center"}
            alignContent={"center"}
            alignItems={"center"}
            spacing={1}
            sx={{
                minHeight: "100vh",
                backgroundColor: "#F5F5F5",
            }}>
            <ProposeMeetingProvider
                userDictionary={userDictionary}
                patientDictionary={patientDictionary}
                isLoading={isLoading}
            >
                {meetingData && isCreator &&
                    <MeetingTimeComponent
                        meetingId={meetingId || ""}
                        userId={userId}
                        joinCode={joinCode}
                        meeting={meetingData}
                        proposedTimes={data.proposedTimes}
                        setMessage={setMessage} />}

                {meetingData && !isCreator &&
                    <ConfirmTime
                        userId={userId}
                        meetingId={meetingId || ""}
                        joinCode={joinCode}
                        creatorId={meetingData.creatorId}
                        proposedTimes={data.proposedTimes || []}
                        setMessage={setMessage}
                    />
                }
            </ProposeMeetingProvider>
        </Stack>
    );
};

export default MeetingTimePage;
